/**
 * ============================================================================
 * Simplified Cart Management Logic
 * ============================================================================
 * Handles promo items, cart updates, section refreshing, and gift wrapping
 */

// --- Configuration ---
// Define these at the very top to ensure they're available for all functions
const SELECTORS = {
  PROMO: ".lt-cart-promo-product",
  CART_SAMPLES: "#lt-cart-samples",
  CART_CAROUSEL: "#lt-cart-product-carousel",
  CART_PROMO_MESSAGE: "#cart-promo-message",
  GIFT_WRAP_CONTENT: ".lt-cart-gift-wrap .gift-content",
  GIFT_WRAP_MESSAGE: "#gift-wrap-message",
  GIFT_WRAP_AGREE: "#CartGiftWrapAgree",
  GIFT_NOTE: "#GiftNote",
  GIFT_BOX_AGREE: "#CartGiftBoxAgree",
  LOADER: "#loader", // Original loader for samples
  LOADER_GIFTWRAP: "#loader-giftwrap", // New loader for gift wrap
  LOADER_PROMO: "#loader-promo", // New loader for promo product
  CHECKOUT_CONTAINER: "#sticky-checkout-container",
  CART_CTAS: ".cart__ctas",
  MAIN_CART_ITEMS: "#main-cart-items",
  MAIN_CART_FOOTER: "#main-cart-footer",
  CART_ICON_BUBBLE: "#cart-icon-bubble",
  PROMO_CODE_MESSAGE: "#promo-code-message",
};

const EVENTS = {
  CART_UPDATED: "cart:updated",
  SECTION_LOAD: "shopify:section:load",
  SECTION_SELECT: "shopify:section:select",
  SHOPIFY_CART_UPDATE: "cart:update",
};

// PubSub events from Dawn theme
window.PUB_SUB_EVENTS = window.PUB_SUB_EVENTS || {
  cartUpdate: "cart:update",
  cartError: "cart:error",
  sectionRender: "section:render",
  cartItemAdded: "cart:item-added",
  cartItemError: "cart:item-error",
};

// Add this at the top of your script, after the constants
window.isReloadScheduled = false;

/**
 * Setup sticky checkout button
 */
function setupStickyCheckout() {
  const ctaElement = document.querySelector(SELECTORS.CART_CTAS);
  const positioner = document.getElementById(
    SELECTORS.CHECKOUT_CONTAINER.slice(1)
  );

  if (!ctaElement || !positioner) return;

  const checkoutBtnCallback = (entries) => {
    entries.forEach((entry) => {
      const isBottomVisible =
        entry.boundingClientRect.bottom <= window.innerHeight;
      positioner.classList.toggle("floating-cta", !isBottomVisible);
    });
  };

  const options = {
    root: null,
    threshold: 1,
  };

  const checkoutBtnObserver = new IntersectionObserver(
    checkoutBtnCallback,
    options
  );
  checkoutBtnObserver.observe(ctaElement);
}

document.addEventListener("scroll", (event) => {
  // Get the elements
  const ctaElement = document.querySelector(".cart__ctas");
  const positioner = document.getElementById("sticky-checkout-container");

  // Callback function
  const checkoutBtnCallback = (entries) => {
    entries.forEach((entry) => {
      const isBottomVisible =
        entry.boundingClientRect.bottom <= window.innerHeight;
      isBottomVisible
        ? positioner.classList.remove("floating-cta")
        : positioner.classList.add("floating-cta");
    });
  };

  // Options and observer
  const options = {
    root: null,
    threshold: 1,
  };
  const checkoutBtnobserver = new IntersectionObserver(
    checkoutBtnCallback,
    options
  );

  checkoutBtnobserver.observe(ctaElement);
});


/**
 * Initialize sliders using Splide library
 */
function initializeSliders() {
  console.log("Initializing all sliders");

  // Initialize sample slider
  const cartSampleDiv = document.querySelector(SELECTORS.CART_SAMPLES);
  if (cartSampleDiv) {
    console.log("Initializing cart samples slider");
    try {
      if (typeof Splide !== "undefined") {
        // Check if Splide is already initialized on this element
        if (!cartSampleDiv.classList.contains("is-initialized")) {
          new Splide(SELECTORS.CART_SAMPLES, {
            perPage: 3,
            gap: "1rem",
            pagination: false,
            breakpoints: {
              640: {
                perPage: 2,
              },
            },
          }).mount();
        }
      } else {
        console.warn(
          "Splide library not loaded yet. Slider initialization skipped."
        );
      }
    } catch (e) {
      console.error("Error initializing cart samples slider:", e);
    }
  }

  // Initialize product carousel
  const cartProductCarousel = document.querySelector(SELECTORS.CART_CAROUSEL);
  if (cartProductCarousel) {
    console.log("Initializing cart product carousel");
    try {
      if (typeof Splide !== "undefined") {
        // Check if Splide is already initialized on this element
        if (!cartProductCarousel.classList.contains("is-initialized")) {
          new Splide(SELECTORS.CART_CAROUSEL, {
            perPage: 4,
            gap: "1rem",
            pagination: false,
            breakpoints: {
              1024: {
                perPage: 3,
              },
              768: {
                perPage: 2,
              },
              480: {
                perPage: 1,
              },
            },
          }).mount();
        }
      } else {
        console.warn(
          "Splide library not loaded yet. Slider initialization skipped."
        );
      }
    } catch (e) {
      console.error("Error initializing cart product carousel:", e);
    }
  }
}

/**
 * Initialize promo handler
 */
function initializePromoHandler() {
  console.log("Initializing promo handler");

  // Run initial check immediately only once
  if (!window.initialPromoCheckDone) {
    performInitialCheck();
    window.initialPromoCheckDone = true;
  }

  // Set up event listeners for quantity changes
  document.addEventListener("change", function (event) {
    if (event.target.matches('input[name="updates[]"]')) {
      const promoElement = document.querySelector(SELECTORS.PROMO);
      if (promoElement && !window.isReloadScheduled) {
        // Give the cart time to update
        setTimeout(() => checkPromoThresholdAndRemove(promoElement), 500);
      }
    }
  });

  // Subscribe to cart update events
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    // Skip if we're reloading or handling our own events
    if (
      window.isReloadScheduled ||
      event.source === "lt-cart-js-reload" ||
      event.source === "lt-cart-js-promo-check"
    ) {
      return;
    }

    console.log(
      "Dawn PubSub cart update event received in promo handler:",
      event
    );

    const promoElement = document.querySelector(SELECTORS.PROMO);
    if (promoElement) {
      setTimeout(() => checkPromoThresholdAndRemove(promoElement), 250);
    }
  });

  // Listen to section load events for theme editor
  document.addEventListener(EVENTS.SECTION_LOAD, function (event) {
    const promoElement = event.target.querySelector(SELECTORS.PROMO);
    if (promoElement) {
      setTimeout(() => checkPromoThresholdAndRemove(promoElement), 50);
    }
  });
}

// Track initialization and store unsubscribers
window.cartInitialized = false;
let cartUpdateUnsubscriber = null;

/**
 * Initialize everything on DOM ready
 */
function initializeAll() {
  // Prevent multiple initializations
  if (window.cartInitialized) {
    console.log("Cart already initialized, skipping");
    return;
  }

  console.log("Initializing all cart components");
  window.cartInitialized = true;
  window.initialPromoCheckDone = false;
  window.isReloadScheduled = false;

  // Set up the main PubSub event handler first
  setupCartEventListeners();

  // Wait a short moment for the DOM to stabilize
  setTimeout(() => {
    // Initialize all components
    initializeCartBlocks();
    initializePromoHandler();
    initializeGiftWrap();
    setupStickyCheckout();

    // Initialize sliders with a more reliable approach
    initializeSliders();

    // Sometimes sliders need a second attempt if the first one fails
    setTimeout(initializeSliders, 500);
  }, 100);
}

/**
 * Set up event listeners using Dawn's PubSub system
 */
function setupCartEventListeners() {
  // Clean up any existing subscription
  if (cartUpdateUnsubscriber) {
    cartUpdateUnsubscriber();
    cartUpdateUnsubscriber = null;
  }

  // Subscribe to cart update events from Dawn
  cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    console.log("Dawn cart update event received:", event);

    // Don't respond to our own events or if we're about to reload
    if (
      event.source === "cart-items" ||
      event.source === "lt-cart-js-reload" ||
      event.source === "lt-cart-js-promo-check" ||
      window.isReloadScheduled
    ) {
      return;
    }

    // Initialize cart blocks and check promo thresholds
    initializeCartBlocks();
    initializePromoHandler();

    // Make sure to re-check gift wrap status after cart updates
    initializeGiftWrap();

    // Dispatch our custom event for backward compatibility
    document.dispatchEvent(new CustomEvent(EVENTS.CART_UPDATED));
  });

  // Subscribe to cart item added events if needed
  subscribe(PUB_SUB_EVENTS.cartItemAdded, (event) => {
    console.log("Dawn cart item added event received:", event);
  });

  // Listen for section renders which happen when cart is updated
  subscribe(PUB_SUB_EVENTS.sectionRender, (event) => {
    console.log("Section render event received:", event);

    // Check if the gift wrap section was re-rendered
    const giftWrapContent = document.querySelector(SELECTORS.GIFT_WRAP_CONTENT);
    if (giftWrapContent) {
      // Re-initialize gift wrap functionality
      setTimeout(() => initializeGiftWrap(), 100);
    }
  });
}

// Run initialization based on document state
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeAll);
} else {
  // Handle cases where the script loads after DOMContentLoaded
  initializeAll();
}

// Backward compatibility event listener
document.addEventListener(EVENTS.SHOPIFY_CART_UPDATE, () => {
  initializeCartBlocks();
  initializePromoHandler();
});

// Make functions available globally
window.sampleBtn = sampleBtn;
window.submitPromo = submitPromo;

/**
 * Clean up event listeners
 */
function cleanupCartEventListeners() {
  if (cartUpdateUnsubscriber) {
    cartUpdateUnsubscriber();
    cartUpdateUnsubscriber = null;
  }
}

// Make this function available globally
window.cleanupCartEventListeners = cleanupCartEventListeners;

// --- Core Utility Functions ---

/**
 * Universal function to fetch cart data
 */
function fetchCart() {
  return fetch(`${window.location.pathname}.js`).then((response) => {
    if (!response.ok)
      throw new Error(`Failed to fetch cart: ${response.statusText}`);
    return response.json();
  });
}

/**
 * Simplified update cart function
 */
function updateCart(params) {
  const { action = "update", data } = params;

  // Determine URL based on action
  let url;
  switch (action) {
    case "add":
      url = `${window.location.pathname}/add.js`;
      break;
    case "change":
      url = `${window.location.pathname}/change.js`;
      break;
    case "update":
    default:
      url = `${window.location.pathname}/update.js`;
  }

  // Create a copy of the data
  const requestData = { ...data };

  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(requestData),
  }).then((response) => {
    if (!response.ok)
      throw new Error(`Network response was not ok: ${response.statusText}`);
    return response.json();
  });
}

/**
 * Reload the page after cart actions
 */
function reloadAfterCartAction(data, delay = 100, queryParam = null) {
  console.log("Scheduling page reload after cart action");

  // Prevent multiple reloads
  if (window.isReloadScheduled) {
    console.log(
      "A page reload is already scheduled, skipping additional reload"
    );
    return data;
  }

  // Mark that we're going to reload
  window.isReloadScheduled = true;

  // Publish the event if not reloading immediately
  if (delay > 50) {
    publish(PUB_SUB_EVENTS.cartUpdate, {
      cart: data,
      source: "lt-cart-js-reload",
    });
  }

  // Force reload after delay
  const reloadTimeout = setTimeout(() => {
    console.log("Executing page reload now!");
    clearTimeout(reloadTimeout);

    // Add query param if provided
    if (queryParam) {
      const reloadUrl = window.location.pathname + "?" + queryParam;
      window.location.href = reloadUrl;
    } else {
      window.location.reload(true); // Force reload from server
    }
  }, delay);

  return data;
}

/**
 * Update cart attributes
 */
function updateCartAttribute(key, value, reload = false) {
  return updateCart({
    action: "update",
    data: { attributes: { [key]: value } },
  }).then((data) => {
    if (reload) {
      return reloadAfterCartAction(data);
    }
    return data;
  });
}

/**
 * Add item to cart - simplified to always reload
 */
function addItemToCart(variantId, quantity = 1) {
  return updateCart({
    action: "add",
    data: {
      items: [{ id: variantId, quantity }],
    },
  }).then((data) => reloadAfterCartAction(data));
}

/**
 * Remove item from cart - simplified to always reload
 */
function removeItemFromCart(variantId) {
  console.log(`Attempting to remove item with variant ID: ${variantId}`);

  return fetchCart().then((cart) => {
    // Use loose comparison to handle string/number type differences
    const itemToRemove = cart.items.find(
      (item) => item.variant_id == variantId
    );

    if (itemToRemove) {
      console.log(
        `Found item to remove: ${itemToRemove.title} (key: ${itemToRemove.key})`
      );

      return updateCart({
        action: "change",
        data: {
          id: itemToRemove.key,
          quantity: 0,
        },
      }).then((data) => reloadAfterCartAction(data));
    } else {
      console.warn(`Item with variantId ${variantId} not found in cart.`);
      // Reload anyway to ensure consistent state
      return reloadAfterCartAction(cart);
    }
  });
}

/**
 * Remove promo item from cart
 */
function removePromoItem(itemKey, promoData) {
  if (!itemKey || !promoData)
    return Promise.reject("Missing required parameters");

  console.log("Attempting to remove promo item with key:", itemKey);

  return fetchCart()
    .then((cart) => {
      // Try to find the item by key
      const lineItem = cart.items.findIndex((item) => item.key === itemKey);
      const promoProductId = parseInt(promoData.promoProductId, 10);

      // If couldn't find by key, try by product ID
      let lineNumber = lineItem + 1; // Line numbers in Shopify are 1-based

      if (lineItem === -1 && promoProductId) {
        const productLineItem = cart.items.findIndex(
          (item) => item.product_id === promoProductId
        );
        if (productLineItem !== -1) {
          lineNumber = productLineItem + 1;
          console.log(
            `Couldn't find item by key, using product ID match instead. Line: ${lineNumber}`
          );
        } else {
          console.warn(
            "Could not find promo item in cart by key or product ID"
          );
          return reloadAfterCartAction(cart);
        }
      }

      return updateCart({
        action: "change",
        data: {
          line: lineNumber,
          quantity: 0,
        },
      }).then((updatedData) => {
        console.log(
          "Cart update response after line item removal:",
          updatedData
        );
        return reloadAfterCartAction(updatedData);
      });
    })
    .catch((error) => {
      console.error("Error during promo removal:", error);
      if (window.showCartError) {
        window.showCartError(
          "Sorry, we could not remove the promo item. Please try again."
        );
      }
      window.location.reload();
      return null;
    });
}

/**
 * Check if promo threshold is met and remove item if not
 */
function checkPromoThresholdAndRemove(promoElement, isInitialCheck = false) {
  if (!promoElement) return;

  const promoData = promoElement.dataset;
  const promoProductId = parseInt(promoData.promoProductId, 10);
  const promoThreshold = parseInt(promoData.promoThreshold, 10);
  const cartUrl = promoData.cartUrl || "/cart";

  if (isNaN(promoProductId) || isNaN(promoThreshold) || promoThreshold <= 0) {
    console.warn(
      "Promo check skipped: Missing or invalid data attributes on",
      promoElement
    );
    return;
  }

  console.log(
    `Checking promo threshold: Product ID ${promoProductId}, Threshold ${promoThreshold}`
  );

  // Show the promo-specific loader
  const loader = document.querySelector(SELECTORS.LOADER_PROMO);
  if (loader) loader.style.display = "block";

  fetchCart()
    .then((cart) => {
      let promoInCart = false;
      let promoItemKey = null;
      let totalWithoutPromo = cart.total_price;

      for (const item of cart.items) {
        console.log(
          `Cart item: ${item.product_id} (${item.title}), comparing to ${promoProductId}`
        );
        if (item.product_id === promoProductId) {
          promoInCart = true;
          promoItemKey = item.key;
          totalWithoutPromo -= item.final_line_price;
          console.log(
            `Found promo item in cart! Key: ${promoItemKey}, Price: ${item.final_line_price}`
          );
          break;
        }
      }

      console.log(
        `Cart total without promo: ${totalWithoutPromo} (${cart.currency}), ` +
          `Threshold: ${promoThreshold} (${cart.currency})`
      );

      if (promoInCart && totalWithoutPromo < promoThreshold) {
        console.log(
          `Threshold NOT MET - removing promo item with key: ${promoItemKey}`
        );

        return updateCart({
          action: "change",
          data: {
            id: promoItemKey,
            quantity: 0,
          },
        }).then((updatedData) => {
          console.log(
            "Cart update response after promo item removal:",
            updatedData
          );
          if (loader) loader.style.display = "none";
          return reloadAfterCartAction(
            updatedData,
            100,
            isInitialCheck ? "promo_check=true" : "promo_removed=true"
          );
        });
      } else {
        if (loader) loader.style.display = "none";

        if (promoInCart) {
          console.log(`Threshold MET - keeping promo item in cart`);
          if (isInitialCheck) {
            const url = new URL(window.location);
            url.searchParams.set("promo_check", "true");
            window.history.replaceState({}, document.title, url);
          }
        } else {
          console.log(`No promo item in cart to check`);
        }
        return cart;
      }
    })
    .catch((error) => {
      console.error("Error checking cart state:", error);
      if (loader) loader.style.display = "none";
    });
}

/**
 * Perform initial check on page load
 */
function performInitialCheck() {
  console.log("Initial check");

  const promoElement = document.querySelector(SELECTORS.PROMO);
  if (!promoElement) {
    console.log("Initial check: No promo section element found.");
    return;
  }

  // Check if there's an explicit removal key
  const initialRemoveKey = promoElement.dataset.promoInitialRemoveKey;
  if (initialRemoveKey) {
    console.log("Initial check: Promo removal key found on page load.");
    return removePromoItem(initialRemoveKey, promoElement.dataset);
  }

  // Verify the promo threshold
  console.log("Running promo threshold verification...");

  const promoProductId = parseInt(promoElement.dataset.promoProductId, 10);
  const promoThreshold = parseInt(promoElement.dataset.promoThreshold, 10);

  if (isNaN(promoProductId) || isNaN(promoThreshold)) {
    console.warn("Invalid promo product ID or threshold");
    return;
  }

  // Show loading indicator
  const loader = document.querySelector(SELECTORS.LOADER);
  if (loader) loader.style.display = "block";

  // Check the cart
  return fetchCart()
    .then((cart) => {
      let promoInCart = false;
      let promoItemKey = null;
      let totalWithoutPromo = cart.total_price;

      // Find promo item in cart
      for (const item of cart.items) {
        if (item.product_id === promoProductId) {
          promoInCart = true;
          promoItemKey = item.key;
          totalWithoutPromo -= item.final_line_price;
          console.log(
            `Found promo item in cart! Key: ${promoItemKey}, Price: ${item.final_line_price}`
          );
          break;
        }
      }

      console.log(
        `Cart total without promo: ${totalWithoutPromo} (${cart.currency}), Threshold: ${promoThreshold} (${cart.currency})`
      );

      // If promo is in cart but threshold isn't met, remove it
      if (promoInCart && totalWithoutPromo < promoThreshold) {
        console.log(
          `THRESHOLD NOT MET - removing promo item with key: ${promoItemKey}`
        );

        return updateCart({
          action: "change",
          data: {
            id: promoItemKey,
            quantity: 0,
          },
        }).then((data) => {
          if (loader) loader.style.display = "none";

          // Dispatch events
          publish(PUB_SUB_EVENTS.cartUpdate, {
            cart: data,
            source: "lt-cart-js-promo-check",
          });
          document.dispatchEvent(new CustomEvent(EVENTS.CART_UPDATED));

          console.log("Promo item removed due to threshold not being met");
          return data;
        });
      } else {
        if (loader) loader.style.display = "none";

        if (promoInCart) {
          console.log(`Threshold MET - keeping promo item in cart`);
        } else {
          console.log(`No promo item in cart to check`);
        }

        return cart;
      }
    })
    .catch((error) => {
      console.error("Error in promo threshold check:", error);
      if (loader) loader.style.display = "none";
    });
}

/**
 * Handle sample button click
 */
function sampleBtn(event, variant, index) {
  const sample = event.target.closest(".sample-product");
  const variantExists = sample.classList.contains("sample-in-cart");
  const loader = document.querySelector(SELECTORS.LOADER);

  // Show loader
  if (loader) loader.style.display = "block";

  if (variantExists) {
    // Remove item and reload
    updateCart({
      action: "change",
      data: {
        line: index,
        quantity: 0,
      },
    })
      .then((data) => {
        if (loader) loader.style.display = "none";
        reloadAfterCartAction(data);
      })
      .catch(() => {
        if (loader) loader.style.display = "none";
        window.location.reload();
      });
  } else {
    // Add item and reload
    updateCart({
      action: "add",
      data: {
        items: [{ id: variant, quantity: 1 }],
      },
    })
      .then((data) => {
        if (loader) loader.style.display = "none";
        reloadAfterCartAction(data);
      })
      .catch(() => {
        if (loader) loader.style.display = "none";
        window.location.reload();
      });
  }
}

/**
 * Submit promo code using the new Cart Ajax API discount endpoint
 */
function submitPromo(event) {
  event.target.classList.add("submitting");
  const spinner = event.target.querySelector(".loading__spinner");
  if (spinner) spinner.classList.remove("hidden");

  const discountCode = document.getElementById("apply-discount").value;
  if (!discountCode) {
    if (spinner) spinner.classList.add("hidden");
    event.target.classList.remove("submitting");
    return;
  }

  async function applyDiscountCode(discountCode) {
    try {
      // Get cart section IDs for updating UI
      const mainCartItems = document.querySelector(SELECTORS.MAIN_CART_ITEMS);
      const mainCartFooter = document.querySelector(SELECTORS.MAIN_CART_FOOTER);

      // Use the new Cart Ajax API discount endpoint to validate and apply discount
      const discountResponse = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          discount: discountCode,
          sections:
            mainCartItems && mainCartFooter
              ? [mainCartItems.dataset.id, mainCartFooter.dataset.id]
              : [],
        }),
      });

      if (!discountResponse.ok) {
        // Handle error response (invalid discount code)
        const errorData = await discountResponse.json();
        console.log("Discount validation failed:", errorData);

        // Show error message
        const promoMessage = document.getElementById(
          SELECTORS.PROMO_CODE_MESSAGE.slice(1)
        );
        if (promoMessage) promoMessage.style.display = "block";
        console.log("Invalid discount code!");

        return { ok: false, error: errorData };
      }

      const responseData = await discountResponse.json();
      console.log("🚀 ~ discount response:", responseData);

      // Check if discount was successfully applied
      if (responseData && discountResponse.status === 200) {
        console.log("Valid discount code applied!");

        // Set the discount cookie for checkout
        document.cookie = `discount_code=${discountCode}; path=/`;

        // Update cart sections if they exist in the response
        if (responseData.sections) {
          if (
            mainCartItems &&
            responseData.sections[mainCartItems.dataset.id]
          ) {
            document.querySelector(".main-cart-items").innerHTML =
              responseData.sections[mainCartItems.dataset.id];
          }
          if (
            mainCartFooter &&
            responseData.sections[mainCartFooter.dataset.id]
          ) {
            document.querySelector(".main-cart-footer").innerHTML =
              responseData.sections[mainCartFooter.dataset.id];
          }
        }

        // Reload the page to show the discount
        return reloadAfterCartAction(responseData);
      } else {
        // Discount code exists but may not be applicable to current cart
        console.log("Discount code not applicable to current cart");

        const promoMessage = document.getElementById(
          SELECTORS.PROMO_CODE_MESSAGE.slice(1)
        );
        if (promoMessage) promoMessage.style.display = "block";

        return { ok: false, error: "Discount not applicable" };
      }
    } catch (error) {
      console.error("Error applying discount code:", error);

      // Show error message
      const promoMessage = document.getElementById(
        SELECTORS.PROMO_CODE_MESSAGE.slice(1)
      );
      if (promoMessage) promoMessage.style.display = "block";

      return { ok: false, error: error.message };
    }
  }

  applyDiscountCode(discountCode)
    .catch((error) => {
      console.error("Discount code error:", error);
    })
    .finally(() => {
      event.target.classList.remove("submitting");
      if (spinner) spinner.classList.add("hidden");
    });
}

/**
 * Initialize cart UI blocks with observer pattern
 */
function initializeCartBlocks() {
  console.log("Initializing cart blocks");

  // First attempt to initialize immediately
  initializeCartBlocksNow();

  // Set up observer for late-rendered elements
  const cartObserver = new MutationObserver((mutations) => {
    const cartSampleDiv = document.querySelector(SELECTORS.CART_SAMPLES);
    const cartProductCarousel = document.querySelector(SELECTORS.CART_CAROUSEL);

    // If either slider element is found, initialize
    if (
      (cartSampleDiv && !cartSampleDiv.classList.contains("is-initialized")) ||
      (cartProductCarousel &&
        !cartProductCarousel.classList.contains("is-initialized"))
    ) {
      console.log(
        "Cart slider elements found in DOM mutation, initializing..."
      );
      initializeCartBlocksNow();

      // Wait to confirm initialization was successful
      setTimeout(() => {
        const sampleInitialized =
          !cartSampleDiv || cartSampleDiv.classList.contains("is-initialized");
        const carouselInitialized =
          !cartProductCarousel ||
          cartProductCarousel.classList.contains("is-initialized");

        // If both are initialized, disconnect observer
        if (sampleInitialized && carouselInitialized) {
          console.log("All sliders initialized, disconnecting observer");
          cartObserver.disconnect();
        }
      }, 500);
    }
  });

  // Start observing the document
  cartObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  // Wait for Splide to be available
  if (typeof Splide === "undefined") {
    console.log("Splide not yet available, waiting...");
    const splideCheckInterval = setInterval(() => {
      if (typeof Splide !== "undefined") {
        console.log("Splide now available");
        clearInterval(splideCheckInterval);
        initializeCartBlocksNow();
      }
    }, 200);
  }
}

/**
 * Immediate initialization of cart UI elements
 */
function initializeCartBlocksNow() {
  const cartSampleDiv = document.querySelector(SELECTORS.CART_SAMPLES);
  const cartProductCarousel = document.querySelector(SELECTORS.CART_CAROUSEL);

  // Initialize sample slider
  if (cartSampleDiv && !cartSampleDiv.classList.contains("is-initialized")) {
    console.log("Initializing cart samples slider");
    try {
      if (typeof Splide !== "undefined") {
        new Splide(SELECTORS.CART_SAMPLES).mount();
        cartSampleDiv.classList.add("is-initialized");
      } else {
        console.warn("Splide library not loaded yet for samples slider");
      }
    } catch (e) {
      console.error("Error initializing cart samples slider:", e);
    }
  }

  // Initialize product carousel
  if (
    cartProductCarousel &&
    !cartProductCarousel.classList.contains("is-initialized")
  ) {
    console.log("Initializing cart product carousel");
    try {
      if (typeof Splide !== "undefined") {
        new Splide(SELECTORS.CART_CAROUSEL).mount();
        cartProductCarousel.classList.add("is-initialized");
      } else {
        console.warn("Splide library not loaded yet for product carousel");
      }
    } catch (e) {
      console.error("Error initializing cart product carousel:", e);
    }
  }

  // Initialize gift message toggle
  const giftMsgDetail = document.querySelector(SELECTORS.GIFT_WRAP_MESSAGE);
  const giftMsgAgree = document.querySelector(SELECTORS.GIFT_WRAP_AGREE);

  if (giftMsgDetail && giftMsgAgree) {
    giftMsgAgree.addEventListener("change", function () {
      if (giftMsgAgree.checked) {
        giftMsgDetail.setAttribute("open", "");
      } else {
        giftMsgDetail.removeAttribute("open");
      }
    });
  }

  // Character counter for gift note
  function charCount() {
    const note = document.getElementById(SELECTORS.GIFT_NOTE.slice(1));
    if (!note) return;

    const textEntered = note.value;
    const msg = document.getElementById("msg");
    if (msg && note.maxLength) {
      const counter = note.maxLength - textEntered.length;
      msg.textContent = counter + " characters left";
    }
  }

  const giftNote = document.getElementById(SELECTORS.GIFT_NOTE.slice(1));
  if (giftNote) {
    giftNote.addEventListener("keyup", charCount, false);
  }
}

/**
 * Gift wrap functionality
 */
function initializeGiftWrap() {
  console.log("Initializing gift wrap functionality");

  // Automatically open the gift note section if a note exists
  const giftNote = document.getElementById(SELECTORS.GIFT_NOTE.slice(1));
  const giftMsgDetail = document.querySelector(SELECTORS.GIFT_WRAP_MESSAGE);
  const giftMsgAgree = document.querySelector(SELECTORS.GIFT_WRAP_AGREE);

  // Check if we have a gift note in the cart already
  if (giftNote && giftMsgDetail && giftMsgAgree) {
    // Check if the note has content
    if (giftNote.value && giftNote.value.trim() !== "") {
      console.log("Gift note found in cart, opening gift note section");

      // Open the details element
      giftMsgDetail.setAttribute("open", "");

      // Check the checkbox
      giftMsgAgree.checked = true;

      // Update character counter right away
      if (typeof window.updateGiftNoteCounter === "function") {
        window.updateGiftNoteCounter(giftNote);
      }
    }
  }

  // Also listen for shopify section loads, which happen when the cart is updated
  document.removeEventListener(EVENTS.SECTION_LOAD, onSectionLoad);
  document.addEventListener(EVENTS.SECTION_LOAD, onSectionLoad);

  // Make the handler function available globally
  window.handleGiftWrap = function (checkbox) {
    // Store the original checked state
    const originalState = checkbox.checked;
    const isChecked = originalState;

    console.log(`Gift wrap checkbox changed to: ${isChecked}`);

    // Get loader
    let loader = document.querySelector(SELECTORS.LOADER_GIFTWRAP);
    if (!loader) {
      loader = document.querySelector(SELECTORS.LOADER);
    }

    // Show loader if it exists
    if (loader) loader.style.display = "block";

    // Get the gift wrap content element
    const giftWrapContent = document.querySelector(SELECTORS.GIFT_WRAP_CONTENT);

    // Always update the cart attribute first
    updateCart({
      action: "update",
      data: { attributes: { "Gift Wrap": isChecked } },
    })
      .then(() => {
        // Only try to add/remove product if we have valid gift wrap content with an ID
        if (
          giftWrapContent &&
          giftWrapContent.dataset.giftWrapId &&
          giftWrapContent.dataset.giftWrapId !== "0"
        ) {
          const giftWrapId = giftWrapContent.dataset.giftWrapId;
          console.log(`Processing gift wrap product with ID: ${giftWrapId}`);

          if (isChecked) {
            // Add gift wrap product
            return updateCart({
              action: "add",
              data: { items: [{ id: giftWrapId, quantity: 1 }] },
            });
          } else {
            // Find and remove the gift wrap product
            return fetchCart().then((cart) => {
              const giftWrapItem = cart.items.find(
                (item) => item.variant_id == giftWrapId
              );
              if (giftWrapItem) {
                return updateCart({
                  action: "change",
                  data: { id: giftWrapItem.key, quantity: 0 },
                });
              }
              return cart;
            });
          }
        } else {
          console.log(
            "No gift wrap product linked or ID is 0, only attribute updated"
          );
          return Promise.resolve(); // No product to add/remove
        }
      })
      .then(() => {
        console.log("Gift wrap processing complete");
        if (loader) loader.style.display = "none";

        // Only reload if we have a product to update
        const hasValidProduct =
          giftWrapContent &&
          giftWrapContent.dataset.giftWrapId &&
          giftWrapContent.dataset.giftWrapId !== "0";

        if (hasValidProduct) {
          window.location.reload();
        }
      })
      .catch((error) => {
        console.error("Error handling gift wrap:", error);
        if (loader) loader.style.display = "none";
        checkbox.checked = !originalState;
        alert("Could not update gift wrap. Please try again.");
      });
  };

  // Initialize gift note handling
  if (giftNote) {
    // Define debounce function
    function debounce(func, delay) {
      let timer;
      return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => func.apply(this, args), delay);
      };
    }

    // Create debounced handler and make it global
    window.handleGiftNoteChange = debounce(function (input) {
      updateCartAttribute("Gift Note", input.value);
    }, 750);

    // Add event listener for gift note textarea
    giftNote.addEventListener("input", function (e) {
      window.handleGiftNoteChange(this);
    });
  }

  // Setup character counter
  window.updateGiftNoteCounter = function (textarea) {
    const msg = document.getElementById("msg");
    if (msg && textarea.maxLength) {
      const counter = textarea.maxLength - textarea.value.length;
      msg.textContent = counter + " characters left";
    }
  };

  // Setup gift message toggle
  window.toggleGiftWrapMessage = function (checkbox) {
    const giftMsgDetail = document.querySelector(SELECTORS.GIFT_WRAP_MESSAGE);
    if (giftMsgDetail) {
      if (checkbox.checked) {
        giftMsgDetail.setAttribute("open", "");
      } else {
        giftMsgDetail.removeAttribute("open");
      }
    }
  };
}

/**
 * Section load handler to check for gift notes
 */
function onSectionLoad(event) {
  // Check if the loaded section contains gift note elements
  const giftNote = event.target.querySelector(SELECTORS.GIFT_NOTE);
  const giftMsgDetail = event.target.querySelector(SELECTORS.GIFT_WRAP_MESSAGE);
  const giftMsgAgree = event.target.querySelector(SELECTORS.GIFT_WRAP_AGREE);

  // If we have all the elements and the note has content
  if (
    giftNote &&
    giftMsgDetail &&
    giftMsgAgree &&
    giftNote.value &&
    giftNote.value.trim() !== ""
  ) {
    console.log(
      "Gift note found in reloaded section, opening gift note section"
    );

    // Open the details element
    giftMsgDetail.setAttribute("open", "");

    // Check the checkbox
    giftMsgAgree.checked = true;

    // Update character counter right away
    if (typeof window.updateGiftNoteCounter === "function") {
      window.updateGiftNoteCounter(giftNote);
    }
  }
}

// PubSub system from Dawn theme
function subscribe(eventName, callback) {
  const eventCallbacks = window.Shopify?.designMode
    ? (window.Shopify.eventCallbacks = window.Shopify.eventCallbacks || {})
    : (document.eventCallbacks = document.eventCallbacks || {});
  eventCallbacks[eventName] = eventCallbacks[eventName] || [];
  eventCallbacks[eventName].push(callback);

  return function unsubscribe() {
    const callbacks = eventCallbacks[eventName];
    const index = callbacks.indexOf(callback);
    if (index !== -1) {
      callbacks.splice(index, 1);
    }
  };
}

function publish(eventName, data) {
  const eventCallbacks = window.Shopify?.designMode
    ? (window.Shopify.eventCallbacks = window.Shopify.eventCallbacks || {})
    : (document.eventCallbacks = document.eventCallbacks || {});

  if (eventCallbacks[eventName]) {
    eventCallbacks[eventName].forEach((callback) => callback(data));
  }
}
